<template>
  <base-wrapper :loading="loading">
    <div v-if="filter.isOmzet" class="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-6 mb-4">
      <BaseWidget label="Omzet" :value="simpleOrders.meta.total_price" />
    </div>
    <base-card title="Transaksi Penjualan">
      <template #action>
        <div class="flex gap-2">
          <base-button
            color="white"
            class="2xl:hidden"
            @click="filterModalVisible = true"
          >
            <Icon icon="heroicons:funnel" />
            Filter
          </base-button>
          <base-button @click="quickOrderModalVisible = true">
            <Icon icon="heroicons:bolt-solid" class="h-4 w-4" />
          </base-button>
          <base-button :to="{ name: 'penjualan.tambah' }">
            <Icon icon="heroicons:plus" class="h-4 w-4" />
            Tambah Transaksi
          </base-button>
        </div>
      </template>

      <div class="space-y-4">
        <div class="hidden flex-wrap items-end justify-end gap-4 2xl:flex">
          <div v-if="!filter.isOmzet" class="self-center mt-5">
            <base-checkbox name="filter_harga_tanpa_kebijakan" label="Harga Tanpa Kebijakan" v-model="filter.displayDefaultAreaPrice" />
          </div>
          <div class="self-center mt-5">
            <base-checkbox name="filter_omzet" label="Omzet" v-model="filter.isOmzet" @change="loadSimpleOrders" />
          </div>
          <base-input v-if="!filter.isOmzet" label="Kantor Pembuat">
            <base-select
              class="w-48"
              :shadow="false"
              :options="filterOfficeCreatorOptions"
              v-model="filter.officeCreator"
              @change="onChangeFilterOfficeCreator"
            />
          </base-input>
          <base-input v-if="!filter.isOmzet" label="Kantor Tujuan">
            <base-select
              class="w-48"
              :shadow="false"
              :options="filterDestinationOfficeOptions"
              v-model="filter.destinationOffice"
              @change="loadSimpleOrders"
            />
          </base-input>
          <base-input v-if="!filter.isOmzet" label="Status Faktur">
            <base-select
              class="w-48"
              :shadow="false"
              :options="filterOrderStatusOptions"
              v-model="filter.orderStatus"
              @change="loadSimpleOrders"
            />
          </base-input>
          <base-input v-if="!filter.isOmzet" label="Laporan Tervalidasi">
            <base-select
              class="w-48"
              :shadow="false"
              :options="filterReportStatusOptions"
              v-model="filter.reportStatus"
              @change="loadSimpleOrders"
            />
          </base-input>
          <period-select
            class="gap-4"
            with-label
            v-model="filter.periodId"
            @change="loadSimpleOrders"
          />
          <base-input
            :with-label="false"
            type="search"
            search
            :shadow="false"
            placeholder="Cari Nomor Faktur"
            v-model="filter.code"
            debounce
            @native-input="loadSimpleOrders"
          />
          <base-input
            :with-label="false"
            type="search"
            search
            :shadow="false"
            placeholder="Cari Nomor Penjualan"
            v-model="filter.referenceCode"
            debounce
            @native-input="loadSimpleOrders"
          />
        </div>

        <datatable
          :total="simpleOrders.meta.page.total"
          :perPage="simpleOrders.meta.page.perPage"
          :currentPage="simpleOrders.meta.page.currentPage"
          :columns="tableColumns"
          label="faktur"
          compact
          @pagechanged="onChangePage"
        >
          <template #tbody="{ classes }">
            <tr
              v-for="simpleOrder in simpleOrders.data"
              :key="simpleOrder.id"
              :class="[
                classes.tr,
                getRowClass(simpleOrder),
                'cursor-pointer hover:bg-green-100',
              ]"
              @click="onClickSimpleOrder(simpleOrder)"
            >
              <td :class="classes.td">
                <order-code-label :order="simpleOrder" />
                <order-date-label
                  :order="simpleOrder"
                  class="text-xs text-gray-500"
                />
              </td>
              <td :class="classes.td">
                {{
                  getSingleIncluded(
                    simpleOrders,
                    simpleOrder.relationships['order'].data.id
                  ).attributes.reference_code ?? '-'
                }}
              </td>
              <td :class="classes.td">
                {{
                  getSingleIncluded(
                    simpleOrders,
                    simpleOrder.relationships['destination-office'].data.id
                  ).attributes.code
                }}
                |
                {{
                  getSingleIncluded(
                    simpleOrders,
                    simpleOrder.relationships['origin-office'].data.id
                  ).attributes.code
                }}
              </td>
              <td :class="[classes.td, 'text-right']">
                {{
                  getSingleIncluded(
                    simpleOrders,
                    simpleOrder.relationships['order'].data.id
                  ).attributes[filter.displayDefaultAreaPrice ? 'default_area_price' : 'grand_total_price'] | toCurrency
                }}
              </td>
              <td :class="[classes.td, 'text-center', 'text-xs']">
                <base-badge size="sm"
                  :color="getReportStatus(simpleOrder).color"
                  >{{ getReportStatus(simpleOrder).text }}</base-badge
                >
              </td>
              <td :class="[classes.td, 'text-center', 'text-xs']" :title="getSingleIncluded(
                simpleOrders,
                simpleOrder.relationships['order'].data.id
              ).attributes.latest_cancel_description">
                <base-badge size="sm" :color="simpleOrder.attributes.order_status === 'Draft'
                    ? 'yellow'
                    : 'blue'
                  ">{{ simpleOrder.attributes.order_status }}</base-badge>
              </td>
              <td :class="[classes.td, 'text-center']">
                {{
                  getSingleIncluded(
                    simpleOrders,
                    simpleOrder.relationships['period'].data.id
                  ).attributes.start_date | formatDate('MMYY')
                }}
              </td>
            </tr>
            <tr v-if="simpleOrders.data.length" :class="classes.tr">
              <td></td>
              <td></td>
              <td :class="[classes.td, 'text-right', 'text-md', 'text-gray-600', 'italic']"><strong>TOTAL</strong></td>
              <td :class="[classes.td, 'text-right', 'text-gray-600']">
                <strong>{{ totalOrderPrice | toCurrency }}</strong>
              </td>
            </tr>
          </template>
        </datatable>
      </div>
    </base-card>

    <view-order-pusat-modal
      :order-id="detailModal.orderId"
      :visible="detailModal.visible"
      :display-default-area-price="filter.displayDefaultAreaPrice"
      @close="detailModal.visible = false"
      @refresh="onRefresh"
    />

    <filter-order-pusat-modal
      :current-period-id="currentPeriodId"
      :visible="filterModalVisible"
      @close="filterModalVisible = false"
      v-model="filter"
      @saved="onFilterModalSaved"
    />

    <QuickOrderModal :visible="quickOrderModalVisible" @close="quickOrderModalVisible = false" />
  </base-wrapper>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import ViewOrderPusatModal from '@/components/order/view-order-pusat-modal.vue';
import PeriodSelect from '@/components/period/period-select.vue';
import FilterOrderPusatModal from '@/components/order/filter-order-pusat-modal.vue';
import OrderDateLabel from '@/components/order/order-date-label.vue';
import OrderCodeLabel from '@/components/order/order-code-label.vue';
import BaseCheckbox from '../../../../components/base/BaseCheckbox.vue';
import QuickOrderModal from '../../../../components/order/QuickOrderModal.vue';
import BaseWidget from '../../../../components/base/BaseWidget.vue';

export default {
  mixins: [requestMixin],
  components: {
    ViewOrderPusatModal,
    PeriodSelect,
    FilterOrderPusatModal,
    OrderDateLabel,
    OrderCodeLabel,
    BaseCheckbox,
    BaseWidget,
    QuickOrderModal
  },
  computed: {
    filterDestinationOfficeOptions() {
      if (
        this.filter.officeCreator === 'kantor_pelayanan' ||
        this.filter.officeCreator === 'stockist_center'
      ) {
        return [
          {
            key: 'null',
            label: 'Semua',
            value: null,
          },
          {
            key: 'stockist',
            label: 'Stockist',
            value: 'stockist',
          },
        ];
      }

      if (this.filter.officeCreator === 'stockist') {
        return [
          {
            key: 'pusat',
            label: 'Pusat',
            value: null,
          },
          {
            key: 'kantor_pelayanan',
            label: 'Kantor Pelayanan',
            value: null,
          },
          {
            key: 'stockist_center',
            label: 'Stockist Center',
            value: null,
          },
        ];
      }

      return [
        {
          key: 'null',
          label: 'Semua',
          value: null,
        },
        {
          key: 'stockist',
          label: 'Stockist',
          value: 'stockist',
        },
        {
          key: 'kyn',
          label: 'KYN',
          value: 'kyn',
        },
        {
          key: 'scn',
          label: 'SCN',
          value: 'scn',
        },
        {
          key: 'prsh',
          label: 'PRSH',
          value: 'prsh',
        },
      ];
    },
    filterOfficeCreatorOptions() {
      if (this.filter.officeCreator === 'stockist') {
        return [
          {
            key: 'null',
            label: 'Semua',
            value: null,
          },
          {
            key: 'stockist',
            label: 'Stockist',
            value: 'stockist',
          },
        ];
      }

      return [
        {
          key: 'null',
          label: 'Semua',
          value: null,
        },
        {
          key: 'pusat',
          label: 'Pusat',
          value: 'pusat',
        },
        {
          key: 'kantor_pelayanan',
          label: 'Kantor Pelayanan',
          value: 'kantor_pelayanan',
        },
        {
          key: 'stockist_center',
          label: 'Stockist Center',
          value: 'stockist_center',
        },
        {
          key: 'stockist',
          label: 'Stockist',
          value: 'stockist',
        },
      ];
    },
    filterOriginOfficeCategory() {
      if (!this.filter.officeCreator) {
        return null
      }

      return {
        pusat: 1,
        kantor_pelayanan: 2,
        stockist_center: 3,
        stockist: 1
      }[this.filter.officeCreator]
    },
    filterOrderStatus() {
      if (this.filter.isOmzet) {
        return {
          'filter[pusat_omzet_period_uuid]': this.filter.periodId
        }
      }

      if (this.filter.reportStatus !== null) {
        if (this.filter.officeCreator === 'pusat') {
          return {
            [this.filter.reportStatus !== 'belum_tervalidasi' ? 'filter[not-order_status]' : 'filter[order_status]']: 'Draft'
          }
        }

        if (this.filter.reportStatus === 'belum_tervalidasi') {
          return {
            'filter[not-order_status]': 'Terlaporkan',
          };
        }

        if (this.filter.reportStatus === 'tervalidasi') {
          return {
            'filter[order_status]': 'Terlaporkan',
          };
        }

        return {
          'filter[order_status]': 'Terlaporkan',
          'filter[destination_office_category_id]':
            this.filter.reportStatus === 'stockist_center' ? 3 : 2,
        };
      }

      if (
        this.filter.orderStatus === null ||
        this.filter.orderStatus === 'hapus'
      ) {
        return null;
      }

      if (this.filter.orderStatus === 'valid') {
        return {
          'filter[not-order_status]':
            'Draft,Batal Faktur Gudang,Batal Faktur Kasir,Terhapus',
        };
      }

      if (this.filter.orderStatus === 'draft') {
        return {
          'filter[order_status]': 'Draft',
        };
      }

      return {
        'filter[order_status]': 'Batal Faktur Gudang,Batal Faktur Kasir',
      };
    },
    filterOrderStatusOptions() {
      return [
        {
          key: 'null',
          label: 'Semua',
          value: null,
        },
        {
          key: 'valid',
          label: 'Valid',
          value: 'valid',
        },
        {
          key: 'draft',
          label: 'Draft',
          value: 'draft',
        },
        {
          key: 'cancel',
          label: 'Batal Faktur',
          value: 'cancel',
        },
        {
          key: 'hapus',
          label: 'Hapus',
          value: 'hapus',
        },
      ];
    },
    filterReportStatusOptions() {
      return [
        {
          key: 'null',
          label: 'Semua',
          value: null,
        },
        {
          key: 'tervalidasi',
          label: 'Tervalidasi',
          value: 'tervalidasi',
        },
        {
          key: 'belum_tervalidasi',
          label: 'Belum Tervalidasi',
          value: 'belum_tervalidasi',
        }
      ];
    },
    filterSearch() {
      if (
        this.filter.officeCreator !== null &&
        this.filter.officeCreator !== 'pusat'
      ) {
        return null;
      }

      if (
        this.filter.destinationOffice === null ||
        this.filter.destinationOffice === 'stockist'
      ) {
        return null;
      }

      return this.filter.destinationOffice;
    },
    loading() {
      return this.loadingSimpleOrders || this.loadingCurentPeriod;
    },
    tableColumns() {
      return [
        {
          id: 'code',
          name: 'Nomor Faktur',
        },
        {
          id: 'referemce_code',
          name: 'Nomor Penjualan',
        },
        {
          id: 'office_code',
          name: 'Kode Stockist | Kode Gudang',
        },
        {
          id: 'total_price',
          name: 'Total Harga',
          theadClass: 'text-right',
        },
        {
          id: 'report_status',
          name: 'Status Laporan',
          theadClass: 'text-center',
        },
        {
          id: 'order_status',
          name: 'Status Faktur',
          theadClass: 'text-center',
        },
        {
          id: 'period',
          name: 'PERIODE',
          theadClass: 'text-center'
        },
      ];
    },
  },
  data() {
    return {
      currentPeriodId: null,
      detailModal: {
        orderId: null,
        visible: false,
      },
      filter: {
        code: null,
        destinationOffice: null,
        officeCreator: 'pusat',
        isOmzet: false,
        orderStatus: null,
        periodId: null,
        reportStatus: null,
        referenceCode: null,
        displayDefaultAreaPrice: false
      },
      filterModalVisible: false,
      loadingCurentPeriod: false,
      loadingSimpleOrders: false,
      simpleOrders: {
        data: [],
        meta: {
          page: {},
        },
      },
      totalOrderPrice: 0,
      quickOrderModalVisible: false
    };
  },
  methods: {
    getRowClass(simpleOrder) {
      const order = this.getSingleIncluded(
        this.simpleOrders,
        simpleOrder.relationships['order'].data.id
      )

      if (!order.attributes.default_area_price) {
        return 'bg-white'
      }

      if (order.attributes.default_area_price === order.attributes.grand_total_price) {
        return 'bg-white'
      }

      return 'bg-yellow-50'
    },
    getReportStatus(simpleOrder) {
      const originOffice = this.getSingleIncluded(
        this.simpleOrders,
        simpleOrder.relationships['origin-office'].data.id
      )
      const destinationOffice = this.getSingleIncluded(
        this.simpleOrders,
        simpleOrder.relationships['destination-office'].data.id
      )

      if (originOffice.attributes.office_type === 'pusat' && destinationOffice.attributes.office_type === 'stockist') {
        return {
          color: simpleOrder.attributes.order_status === 'Draft' ? 'yellow' : 'blue',
          text: simpleOrder.attributes.order_status === 'Draft' ? 'Belum Tervalidasi' : 'Tervalidasi'
        }
      }

      return {
        color: simpleOrder.attributes.order_status !== 'Terlaporkan' ? 'yellow' : 'blue',
        text: simpleOrder.attributes.order_status !== 'Terlaporkan' ? 'Belum Tervalidasi' : 'Tervalidasi'
      }
    },
    async loadCurrentPeriod() {
      this.loadingCurentPeriod = true;

      const [res, err] = await this.request(
        '/api/v1/periods/-actions/get-current-period'
      );

      if (!err) {
        this.filter.periodId = res.data.id;
        this.currentPeriodId = res.data.id;
      }

      this.loadingCurentPeriod = false;
    },
    async loadSimpleOrders(params) {
      this.loadingSimpleOrders = true;

      const [res, err] = await this.request('/api/v1/orders', {
        params: {
          include: 'destination-office,origin-office,order,period',
          'fields[simple-orders]':
            'origin_code,order_type,destination_code,orderedAt,createdAt,destination-office,origin-office,order,order_status,period',
          'fields[offices]': 'code,office_type',
          'fields[orders]': 'reference_code,grand_total_price,default_area_price,latest_cancel_description',
          'fields[periods]': 'start_date',
          'page[size]': 5,
          'filter[origin_office_category_id]': this.filter.isOmzet ? null : this.filterOriginOfficeCategory,
          'filter[destination_office_category_id]': 4,
          sort: '-id',
          'filter[origin_code]': this.filter.code || null,
          'filter[office_type_creator]': this.filter.isOmzet ? null : this.filter.officeCreator,
          'filter[search]': this.filterSearch,
          'filter[trashed]': this.filter.orderStatus === 'hapus' && !this.filter.isOmzet,
          'filter[period_uuid]': this.filter.isOmzet ? null : this.filter.periodId,
          'filter[with_summary]': true,
          'filter[reference_code]': this.filter.referenceCode || null,
          'filter[is_all_transfer_promo]': false,
          ...this.filterOrderStatus,
          ...params,
        },
      });

      if (!err) {
        this.simpleOrders = res;

        if (res.meta.total_price) {
          this.totalOrderPrice = res.meta.total_price;
        }
      }

      this.loadingSimpleOrders = false;
    },
    onChangeFilterOfficeCreator() {
      if (
        this.filter.officeCreator !== null &&
        this.filter.officeCreator !== 'pusat'
      ) {
        this.filter.destinationOffice = null;
      }

      this.loadSimpleOrders();
    },
    onChangePage(page) {
      this.loadSimpleOrders({
        'page[number]': page,
        'filter[with_summary]': false,
      });
    },
    onClickSimpleOrder(simpleOrder) {
      this.detailModal.visible = true;
      this.detailModal.orderId = simpleOrder.relationships.order.data.id;
    },
    onRefresh() {
      this.detailModal.visible = false;
      this.detailModal.orderId = null;

      this.loadSimpleOrders();
    },
    onFilterModalSaved(value) {
      this.filter.code = value.code;
      this.filter.destinationOffice = value.destinationOffice;
      this.filter.officeCreator = value.officeCreator;
      this.filter.orderStatus = value.orderStatus;
      this.filter.periodId = value.periodId;
      this.filter.reportStatus = value.reportStatus;
      this.filter.referenceCode = value.referenceCode
      this.filter.displayDefaultAreaPrice = value.displayDefaultAreaPrice
      this.filter.isOmzet = value.isOmzet

      this.filterModalVisible = false;

      this.loadSimpleOrders();
    },
  },
  async created() {
    await this.loadCurrentPeriod();

    this.loadSimpleOrders();
  },
};
</script>
